import Colors from '../utilities/Color';

export default class LoanGraphData {
	static infinityLimit = 12;

	/** How many of the latest month-to-month balance drops are averaged into the recent payment. */
	static recentPaymentMonths = 3;

	constructor(category) {
		this.category = category;
		this.budgetHistory = category.getBudgetsInOrder(); // Get the entire history of Home Depot so that we can graph this
		this.firstBudgetWithFund = null;

		this.startingBalance = null;
		this.amountRemaining = null;
		this.amountPreviously = null;
		this.currentDate = null;
		this.lastKnownInterestAmount = null;
		this.lastKnownMinimumPayment = null;

		this.balanceTotals = new Map();
		this.interestsAdded = new Map();
		this.paymentsMade = new Map();
		this.manualUpdates = new Map();

		this.warnings = new Set();
		this.totalInterestPaid = 0;
		this.lastBudgetWithFund = null;

		this.totalMonthsIterated = 0;
		this.monthsIteratedPastKnownValues = 0;
		this.infinityErrors = 0;

		// The rate the loan is on now, and the month it began. A loan whose rate changed part way, a
		// refinance, is summed from that month: its principal is the balance there and its interest
		// paid is what accrues from then on, not the earlier terms'.
		this.currentRate = null;
		this.termsStartDate = null;

		// When set, months past the last known balance are paid at what the loan has actually been
		// paid lately, when that is more than its minimum. Set by LoanPayoff before projecting.
		this.assumeRecentPayments = false;
		// The payment subtractMinimum last made, which is what a strategy rolls over once the loan is gone.
		this.scheduledPayment = null;
		this.lastPaymentDate = null;

		for(let budget of this.budgetHistory) {
			if(!this.firstBudgetWithFund) {
				if(budget.fund_total) {
					this.firstBudgetWithFund = budget;

					this.startingBalance = budget.fund_total;
					this.amountRemaining = budget.fund_total;
					this.amountPreviously = budget.fund_total;
					this.startDate = budget.date.clone();
					this.lastKnownInterestAmount = budget.interest_amount;
					this.lastKnownMinimumPayment = budget.minimum_payment;
				}
			}
			if(budget.fund_total) {
				this.lastBudgetWithFund = budget;
			}
			// A month with no rate arrives as '' (Budget turns null into that), and one '' in the
			// sum turns it into a string and the average into NaN.
			const interest = parseFloat(budget.interest_amount);
			if(Number.isFinite(interest)) {
				if(this.currentRate !== null && interest !== this.currentRate) {
					this.termsStartDate = budget.date.clone();
				}
				this.currentRate = interest;
			}
		}

		this.recentPayment = this.calculateRecentPayment();

		this.currentDataSet = {
			label: this.fullName(),
			name: category.name,
			data: new Map(), // Gets converted into an array before getting passed to chartjs
			payments: this.paymentsMade, // Converted alongside data, for the tooltip
			fill: false,
			radius: 0,
			borderColor: Colors.getDarkColorRgba(category.id),
			backgroundColor: Colors.getDarkColorRgba(category.id),
			lineTension: 0.1
		};
	}


	/**
	 * What the loan has been paid a month lately, read off its balances: each pair of consecutive
	 * months with a balance gives last month's balance, plus a month's interest on it, less this
	 * month's. The latest few are averaged. A month the balance rose (a new draw) says nothing about
	 * the payment and is left out. Null when no two consecutive months both have a balance.
	 */
	calculateRecentPayment() {
		const payments = [];
		let previous = null;
		for(let budget of this.budgetHistory) {
			if(!budget.fund_total) {
				continue;
			}
			if(previous && budget.date.diff(previous.date.clone().startOf('month'), 'months') === 1) {
				const rate = parseFloat(previous.interest_amount) || parseFloat(budget.interest_amount) || 0;
				const balance = Number(previous.fund_total);
				const payment = balance + (balance * rate / 100 / 12) - Number(budget.fund_total);
				if(payment > 0) {
					payments.push(payment);
				}
			}
			previous = budget;
		}
		const recent = payments.slice(-LoanGraphData.recentPaymentMonths);
		if(!recent.length) {
			return null;
		}
		return Math.round(recent.reduce((sum, payment) => sum + payment, 0) / recent.length);
	}

	/** The payment the projection makes in a month: the minimum, or the recent payment once past the known balances. */
	paymentFor(date) {
		const minimum = Number(this.lastKnownMinimumPayment) || 0;
		if(!this.assumeRecentPayments || !this.recentPayment || this.recentPayment <= minimum) {
			return minimum;
		}
		if(this.lastBudgetWithFund && !date.isAfter(this.lastBudgetWithFund.date, 'month')) {
			return minimum;
		}
		return this.recentPayment;
	}

	/** The payment the loan will carry on at, for a summary: the recent payment if it is assumed and higher. */
	ongoingPayment() {
		const minimum = Number(this.lastKnownMinimumPayment) || 0;
		if(this.assumeRecentPayments && this.recentPayment > minimum) {
			return this.recentPayment;
		}
		return minimum;
	}

	/**
	 * What was actually paid toward the loan in a month: its category's transactions then, escrow and
	 * fees included. paymentsMade is only the part the balance accounts for. Null for a month with no
	 * transactions, and for an ad hoc loan, which has none to read.
	 */
	paidIn(date) {
		if(!this.category.transactionFinder) {
			return null;
		}
		const paid = this.category.getBudget(date).getSpent();
		return paid ? paid : null;
	}

	/**
	 * The latest payment as of `date`, looking back a few months, so a month not yet paid shows the
	 * one before it: `{ date, amount }`, or null when none of those months has one.
	 */
	lastPaid(date) {
		for(let back = 0; back < LoanGraphData.recentPaymentMonths; back++) {
			const month = date.clone().subtract(back, 'months');
			const amount = this.paidIn(month);
			if(amount !== null) {
				return { date: month, amount };
			}
		}
		return null;
	}

	/** The balance at the end of the given month, or null when the graph has none for it. */
	balanceAt(date) {
		for(let [month, balance] of this.balanceTotals) {
			if(month.isSame(date, 'month')) {
				return balance;
			}
		}
		return null;
	}

	/** Sums a per-month map over the months from `date` on. */
	static sumFrom(map, date) {
		let total = 0;
		for(let [month, amount] of map) {
			if(!month.isBefore(date, 'month')) {
				total += Number(amount) || 0;
			}
		}
		return total;
	}

	beginsAt(date) {
		return this.startDate.isSame(date, 'month');
	}

	/**
	 * By month: a budget's date is the month at whatever time of day its row was built, so a loan
	 * whose first row was built a millisecond after the projection's first month would otherwise
	 * start a month late and drop its opening month from the schedule.
	 */
	hasStartedAt(date) {
		return this.startDate.isSameOrBefore(date, 'month');
	}

	fullName() {
		if(this.currentRate === null) {
			return this.category.name;
		}
		return `${this.category.name} ${this.currentRate}%`;
	}

	/** The month the current terms began: the latest change of rate, or the first balance. */
	termsStart() {
		return this.termsStartDate || this.startDate;
	}

	/** Before the current terms began, so not part of this loan's principal or interest paid. */
	isBeforeCurrentTerms(date) {
		return !!this.termsStartDate && date.isBefore(this.termsStartDate, 'month');
	}

	applyExtraPayment(currentDate, amount, showDebug = false) {

		const lastBalance = this.balanceTotals.get(currentDate);
		const log = showDebug ? console.log : () => {
		};

		log('applying at', currentDate.calendar());
		log(this.fullName(), amount >= 0 ? 'should subtract' : 'should add ', amount, 'from', this.amountRemaining);
		let remainder = null;
		if(amount >= this.amountRemaining) {
			remainder = amount - this.amountRemaining;
			//Update last payment thingy...
			log('set balance from', lastBalance, ' to 0, remainder:', remainder);
			this.amountRemaining = 0;
			this.balanceTotals.set(currentDate, this.amountRemaining);
		}
		else {
			this.amountRemaining -= amount;
			this.balanceTotals.set(currentDate, this.amountRemaining);

			log('we end up with', this.amountRemaining);
			remainder = 0;
		}
		const budget = this.category.getBudget(currentDate, false);
		if(budget.fund_total) {
			this.manualUpdates.set(currentDate, true);
		}
		else {
			this.manualUpdates.set(currentDate, false);
		}

		if(currentDate.isAfter(this.lastBudgetWithFund.date) && !budget.fund_total) {
			this.monthsIteratedPastKnownValues++;
			if(amount > 0 && this.amountRemaining >= this.amountPreviously && this.monthsIteratedPastKnownValues > LoanGraphData.infinityLimit) {
				this.infinityErrors++;
				this.warnings.add(`${budget.category.name} cannot be paid off with the current minimum payment`);
			}
		}
		this.amountPreviously = this.amountRemaining;

		return remainder;
	}

	getLastKnownInterestAmount() {
		return this.lastKnownInterestAmount;
	}

	calculateCurrentMonth(currentDate) {
		if(!this.started && !this.hasStartedAt(currentDate)) {
			this.balanceTotals.set(currentDate, null);
			return;
		}
		if(this.isFinished()) {
			return;
		}
		this.started = true;
		const budget = this.category.getBudget(currentDate, false);
		if(this.termsStartDate && this.termsStartDate.isSame(currentDate, 'month')) {
			this.startingBalance = budget.fund_total || this.amountRemaining;
		}
		if(budget.interest_amount) {
			this.lastKnownInterestAmount = budget.interest_amount;
		}
		if(budget.minimum_payment) {
			this.lastKnownMinimumPayment = budget.minimum_payment;
		}


		this.balanceTotals.set(currentDate, this.amountRemaining);


		this.currentDataSet.data = this.balanceTotals;
		this.totalMonthsIterated++;
	}

	matchBudgetIfNecessary(currentDate) {
		const budget = this.category.getBudget(currentDate, false);
		if(budget.fund_total) {
			// A recorded balance below where the payment left it means more was paid than that, and the
			// difference is part of the month's payment. A loan can also go up (a new draw), but the
			// payment is never shown below nothing. Savings count deposits the other way, so are left be.
			if(this.type !== 'SAVINGS' && this.paymentsMade.has(currentDate)) {
				const paidMore = this.amountRemaining - Number(budget.fund_total);
				this.paymentsMade.set(currentDate, Math.max(0, this.paymentsMade.get(currentDate) + paidMore));
			}
			this.amountRemaining = budget.fund_total;

			this.balanceTotals.set(currentDate, this.amountRemaining);
			return this.amountRemaining;
		}
		return null;
	}

	addTo(map, currentDate, amount) {
		map.set(currentDate, (map.get(currentDate) || 0) + amount);
	}

	addPayment(currentDate, payment) {
		const remainder = this.applyExtraPayment(currentDate, payment);
		this.addTo(this.paymentsMade, currentDate, payment - remainder);
		this.lastPaymentDate = currentDate;
		return remainder;
	}

	addInterest(currentDate) {
		if(this.isFinished()) {
			return null;
		}
		const thisMonthsInterest = this.amountRemaining * (this.lastKnownInterestAmount / 100 / 12);


		this.addTo(this.interestsAdded, currentDate, thisMonthsInterest);
		this.applyExtraPayment(currentDate, -Math.abs(thisMonthsInterest));
		if(this.isBeforeCurrentTerms(currentDate)) {
			return 0;
		}
		this.totalInterestPaid += thisMonthsInterest;
		return thisMonthsInterest;
	}

	/**
	 * @returns Number Last Known Minimum Payment
	 */
	subtractMinimum(currentDate) {
		if(this.isFinished()) {
			return null;
		}
		this.scheduledPayment = this.paymentFor(currentDate);
		this.addPayment(currentDate, this.scheduledPayment);
		return this.scheduledPayment;
	}

	isFinished() {
		if(this.infinityErrors > LoanGraphData.infinityLimit) {
			return true;
		}
		return this.amountRemaining <= 0;
	}
}